import { useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import {
  ChevronRight,
  Headphones,
  Loader2,
  LockKeyhole,
  Mail,
  MapPin,
  ShieldCheck,
} from 'lucide-react'

import { BrandLockup } from '../../components/Brand'
import { ErrorNote } from '../../components/ui/callout'
import planetUrl from '../../assets/login/planet-cutout.png'
import { ApiError, devLogin } from '../../lib/api'
import { cn } from '../../lib/utils'

const offices = [
  { name: 'Princeton', region: 'HQ' },
  { name: 'Michigan', region: 'USA' },
  { name: 'Georgia', region: 'USA' },
  { name: 'Mexico', region: 'Mexico' },
  { name: 'Gurugram', region: 'India' },
  { name: 'Hyderabad', region: 'India' },
  { name: 'Bengaluru', region: 'India' },
  { name: 'Noida', region: 'India' },
]

function MicrosoftMark() {
  return (
    <svg viewBox="0 0 21 21" className="size-4 shrink-0" aria-hidden="true">
      <rect x="1" y="1" width="9" height="9" fill="#f25022" />
      <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
      <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
      <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
    </svg>
  )
}

export function Login() {
  const [params] = useSearchParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [email, setEmail] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [devOpen, setDevOpen] = useState(false)

  // SPEC section 2: a denied login gets a neutral message with no hint about
  // whether the account exists or which rule refused it.
  const denied = params.get('error') === 'denied'

  async function submitDevLogin(event: React.FormEvent) {
    event.preventDefault()
    setBusy(true)
    setError(null)
    try {
      await devLogin(email)
      await queryClient.invalidateQueries({ queryKey: ['me'] })
      navigate('/', { replace: true })
    } catch (err) {
      setError(
        err instanceof ApiError && err.status === 404
          ? 'Developer sign-in is disabled. Use Microsoft sign-in.'
          : err instanceof Error
            ? err.message
            : 'Sign-in failed.',
      )
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="relative min-h-screen overflow-hidden bg-[#070719] text-[#f8f4ff]">
      <a
        href="#login-panel"
        className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-50 focus:rounded-md focus:bg-white focus:px-3 focus:py-2 focus:text-sm focus:font-medium focus:text-[#070719]"
      >
        Skip to sign in
      </a>
      <div className="lp-login-stars absolute inset-0" aria-hidden="true" />
      <header className="relative z-20 mx-auto flex w-full max-w-7xl items-center justify-between gap-4 px-5 py-5 sm:px-8 lg:px-10">
        <a href="/" className="flex min-w-0 items-center gap-3" aria-label="Logic Planet ITSM home">
          <BrandLockup className="h-11 shadow-[0_0_26px_rgba(255,153,51,0.28)]" />
          <span className="hidden text-sm font-semibold text-white/90 sm:inline">
            Logic Planet ITSM
          </span>
        </a>
        <div className="flex items-center gap-2">
          <span className="hidden rounded-full border border-[#ff9933]/35 bg-[#ff9933]/10 px-3 py-1 text-xs font-semibold text-[#ffd0a3] sm:inline-flex">
            25 Years
          </span>
          <a
            href="mailto:itsupport@logicplanet.com"
            className="inline-flex min-h-9 items-center gap-2 rounded-full border border-white/[0.12] bg-white/[0.07] px-3 text-xs font-semibold text-white/90 backdrop-blur transition-colors hover:border-[#5eead4]/45 hover:bg-white/10 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#5eead4]"
          >
            <Headphones className="size-3.5" aria-hidden="true" />
            Support
          </a>
        </div>
      </header>

      <main className="relative z-10 mx-auto grid min-h-[calc(100vh-5rem)] w-full max-w-7xl items-center gap-8 px-5 pb-8 pt-2 sm:px-8 lg:grid-cols-[minmax(0,1fr)_minmax(360px,430px)] lg:px-10 lg:pb-14">
        <section className="relative order-2 min-h-[320px] lg:order-none lg:min-h-[620px]" aria-labelledby="login-title">
          <div
            className="pointer-events-none absolute left-[72%] top-[42%] aspect-square w-[min(72vw,580px)] -translate-x-1/2 -translate-y-1/2 opacity-70 sm:left-[68%] lg:left-[66%] lg:w-[min(48vw,620px)] lg:opacity-95"
            aria-hidden="true"
          >
            <div
              className="lp-login-orbit absolute inset-0 rounded-full border border-dashed border-[#ff9933]/25"
            >
              <span className="absolute left-1/2 top-0 size-3 -translate-x-1/2 -translate-y-1/2 rounded-full bg-[#5eead4] shadow-[0_0_18px_rgba(94,234,212,0.9)]" />
            </div>
            <div
              className="lp-login-orbit lp-login-orbit-slow absolute inset-[13%] rounded-full border border-[#e9e4ff]/10"
            >
              <span className="absolute left-1/2 top-0 size-4 -translate-x-1/2 -translate-y-1/2 rounded-full bg-[#ff9933] shadow-[0_0_22px_rgba(255,153,51,0.8)]" />
            </div>
            <img
              src={planetUrl}
              alt=""
              className="lp-login-drift absolute inset-[14%] size-[72%] object-contain opacity-95 drop-shadow-[0_0_70px_rgba(255,153,51,0.35)]"
            />
          </div>

          <div className="relative z-10 max-w-2xl py-4 sm:py-8 lg:py-16">
            <p className="lp-login-rise inline-flex items-center gap-2 font-mono text-xs font-semibold uppercase text-[#ffb56c]">
              <span aria-hidden="true" className="h-px w-7 bg-[#ff9933]" />
              Welcome
            </p>
            <h1
              id="login-title"
              className="lp-login-rise mt-3 max-w-2xl text-4xl font-semibold leading-[1.02] text-white [animation-delay:80ms] sm:text-6xl lg:text-7xl"
            >
              Logic Planet ITSM
            </h1>
            <p className="lp-login-rise mt-5 max-w-xl text-base leading-7 text-[#d6d0ea] [animation-delay:140ms] sm:text-lg">
              Sign in with your Microsoft account to raise tickets, track requests, and stay connected to the service desk.
            </p>

            <div className="lp-login-rise mt-8 grid max-w-xl gap-3 rounded-lg border border-white/10 bg-[#090b20]/70 p-4 backdrop-blur [animation-delay:220ms] sm:grid-cols-3">
              <div>
                <p className="font-mono text-[11px] font-semibold uppercase text-[#a9a2c7]">
                  Access
                </p>
                <p className="mt-1 text-sm font-semibold text-white">Microsoft Entra ID</p>
              </div>
              <div>
                <p className="font-mono text-[11px] font-semibold uppercase text-[#a9a2c7]">
                  Coverage
                </p>
                <p className="mt-1 text-sm font-semibold text-white">8 office locations</p>
              </div>
              <div>
                <p className="font-mono text-[11px] font-semibold uppercase text-[#a9a2c7]">
                  Desk
                </p>
                <p className="mt-1 text-sm font-semibold text-white">Tickets and requests</p>
              </div>
            </div>

            <ul className="lp-login-rise mt-5 flex max-w-2xl flex-wrap gap-2 [animation-delay:280ms]" aria-label="Logic Planet office locations">
              {offices.map((office) => (
                <li
                  key={office.name}
                  className="inline-flex min-h-8 items-center gap-2 rounded-full border border-white/10 bg-[#0d1027]/[0.78] px-3 text-xs text-[#f8f4ff] backdrop-blur"
                >
                  <MapPin className="size-3.5 text-[#5eead4]" aria-hidden="true" />
                  <span>{office.name}</span>
                  <span className="font-mono text-[10px] uppercase text-[#ffb56c]">{office.region}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>

        <section
          id="login-panel"
          className="lp-login-rise relative order-1 w-full rounded-lg border border-white/[0.12] bg-[#0b0d23]/[0.92] p-6 shadow-[0_28px_100px_rgba(0,0,0,0.52)] backdrop-blur-xl [animation-delay:180ms] sm:p-8 lg:order-none"
          aria-label="Sign in"
        >
          <div
            className="pointer-events-none absolute inset-x-8 top-0 h-px bg-[linear-gradient(90deg,transparent,#ff9933,#5eead4,transparent)]"
            aria-hidden="true"
          />
          <div className="mb-7 flex items-start justify-between gap-4">
            <div>
              <p className="font-mono text-xs font-semibold uppercase text-[#ffb56c]">
                Secure access
              </p>
              <h2 className="mt-2 text-2xl font-semibold text-white">Sign in</h2>
              <p className="mt-1 text-sm text-[#a9a2c7]">Use the same account you use for Outlook and Teams.</p>
            </div>
            <span className="grid size-11 shrink-0 place-items-center rounded-lg border border-[#5eead4]/30 bg-[#5eead4]/10 text-[#5eead4]">
              <ShieldCheck className="size-5" aria-hidden="true" />
            </span>
          </div>

          {denied && (
            <ErrorNote
              className="mb-5 border-red-400/35 bg-red-500/10 text-red-100"
              message="We could not sign you in. Please contact your IT administrator."
            />
          )}

          <a
            href="/oidc/authenticate/"
            className="group relative inline-flex min-h-12 w-full items-center justify-center gap-3 overflow-hidden rounded-full border border-[#ff9933]/50 bg-[#05040f] px-6 text-sm font-semibold text-white shadow-[0_0_30px_rgba(255,153,51,0.2)] transition-all hover:border-[#ff9933] hover:shadow-[0_0_44px_rgba(255,153,51,0.36)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#ff9933]"
          >
            <span
              aria-hidden="true"
              className="absolute inset-x-6 bottom-0 h-px bg-[linear-gradient(90deg,transparent,#ff9933,transparent)] opacity-90"
            />
            <MicrosoftMark />
            Sign in with Microsoft
          </a>

          <div className="mt-4 flex items-center gap-2 rounded-lg border border-[#5eead4]/[0.18] bg-[#5eead4]/[0.07] px-3 py-2.5 text-xs leading-5 text-[#c9f7ef]">
            <LockKeyhole className="size-4 shrink-0 text-[#5eead4]" aria-hidden="true" />
            Protected by your company Microsoft sign-in policy.
          </div>

          <div className="my-6 h-px w-full bg-[linear-gradient(90deg,transparent,rgba(233,228,255,0.22),transparent)]" />

          <button
            type="button"
            onClick={() => setDevOpen((value) => !value)}
            aria-expanded={devOpen}
            aria-controls="developer-sign-in"
            className="inline-flex items-center gap-1.5 text-xs font-medium text-[#a9a2c7] transition-colors hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#5eead4]"
          >
            <ChevronRight
              className={cn('size-3.5 transition-transform', devOpen && 'rotate-90')}
              aria-hidden="true"
            />
            Developer sign-in
          </button>

          {devOpen && (
            <form id="developer-sign-in" onSubmit={submitDevLogin} className="mt-4 grid gap-3">
              <p className="text-xs leading-5 text-[#a9a2c7]">
                Local development only. Disabled unless the server runs with DEBUG and DEV_LOGIN_ENABLED.
              </p>
              <label className="grid gap-2">
                <span className="pl-4 font-mono text-[11px] font-semibold uppercase text-[#a9a2c7]">
                  Email
                </span>
                <span className="relative">
                  <Mail
                    className="pointer-events-none absolute left-3.5 top-1/2 size-4 -translate-y-1/2 text-[#a9a2c7]"
                    aria-hidden="true"
                  />
                  <input
                    type="email"
                    required
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                    placeholder="you@logicplanet.com"
                    aria-label="Developer sign-in email"
                    className="h-12 w-full rounded-lg border border-white/15 bg-[#05040f]/80 pl-10 pr-3 text-sm text-white placeholder:text-[#a9a2c7]/70 transition-colors hover:border-white/25 focus:border-[#5eead4]/70 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#5eead4]/35"
                  />
                </span>
              </label>
              <button
                type="submit"
                disabled={busy}
                className="inline-flex min-h-11 items-center justify-center gap-2 rounded-full border border-white/15 bg-white px-6 text-sm font-semibold text-[#070719] transition-colors hover:bg-[#f2efff] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#5eead4] disabled:cursor-wait disabled:opacity-80"
              >
                {busy ? <Loader2 className="size-4 animate-spin" aria-hidden="true" /> : null}
                {busy ? 'Signing in' : 'Go'}
              </button>
              {error && <ErrorNote className="border-red-400/35 bg-red-500/10 text-red-100" message={error} />}
            </form>
          )}
        </section>
      </main>
    </div>
  )
}
